import { useCallback } from 'react';

import { Platform, StyleSheet } from 'react-native';

import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { AvoidSoftInput } from 'react-native-avoid-softinput';
import { useMedia, YStack } from 'tamagui';
import { useShallow } from 'zustand/react/shallow';

import { Icon } from '@south-street-app/atoms';
import { ScreenWrapper } from '@south-street-app/molecules';
import { useMobileTradeStore } from '@south-street-app/stores';
import { isAndroid } from '@south-street-app/utils';

import { ConversationChatView } from './ConversationChatView';

const styles = StyleSheet.create({
  pressStyle: {
    opacity: 0.7,
  },
});

const FakeHeader = () => {
  const { goBack } = useNavigation();
  const { desktop } = useMedia();
  const handleGoBack = useCallback(() => {
    // Workaround to avoid bottom sheet appearing on Android when using an input
    if (isAndroid) {
      AvoidSoftInput.setAdjustPan();
    }

    goBack();
  }, [goBack]);

  return (
    <YStack
      paddingVertical={'$4'}
      onPress={handleGoBack}
      {...(Platform.OS === 'web' ? { cursor: 'pointer' } : undefined)}
      pressStyle={styles.pressStyle}
      marginLeft={desktop ? 0 : -10}
      width={'$fit-content'}
    >
      <Icon color={'$mono700'} size={'$6'} iconName={'chevronLeftIcon'} />
    </YStack>
  );
};

const ChatScreen = () => {
  const { desktop } = useMedia();
  const { sendChatMessage, chatConversation, chatTrader } = useMobileTradeStore(
    useShallow((state) => ({
      sendChatMessage: state.sendChatMessage,
      chatConversation: state.chatConversation,
      chatTrader: state.chatTrader,
    })),
  );

  useFocusEffect(() => {
    // Workaround to fix InputToolbar being overlapped by the keyboard
    if (isAndroid) {
      AvoidSoftInput.setAdjustResize();
    }

    return () => {
      AvoidSoftInput.setAdjustPan();
    };
  });

  const onSendMessage = useCallback(
    async (message: string) => {
      await sendChatMessage(message);
    },
    [sendChatMessage],
  );

  return (
    <ScreenWrapper>
      <YStack
        flex={1}
        {...(desktop && {
          backgroundColor: '$brand500',
        })}
      >
        <YStack
          flex={1}
          backgroundColor={'$mono100'}
          paddingHorizontal={'$4'}
          {...(desktop && {
            width: '$200',
            marginHorizontal: '$auto',
            marginVertical: '$4',
            borderRadius: '$true',
            paddingBottom: '$3',
          })}
        >
          {desktop ? <FakeHeader /> : null}
          <ConversationChatView
            avatarName={chatTrader || 'Trader'}
            conversation={chatConversation}
            emptyMessage={'Trade chat is not available yet.'}
            onSendMessage={onSendMessage}
            placeholder={'Message trader'}
          />
        </YStack>
      </YStack>
    </ScreenWrapper>
  );
};

export { ChatScreen };
